import { Checkbox } from '@/ui/base/checkbox';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Textarea } from '@/ui/base/textarea';

import type { CampoDeFormulario } from './cadastros.definicoes';
import { useOpcoesDeMarca, useOpcoesDeModelo } from './cadastros.dados';
import type { ValoresDoFormulario } from './useFormularioDoCadastro';

export interface CamposDoCadastroProps {
  campos: readonly CampoDeFormulario[];
  valores: ValoresDoFormulario;
  problemas: Readonly<Record<string, string>>;
  aoAlterar: (nome: string, valor: string) => void;
  /** Prefixo dos `id` dos campos: dois formulários na mesma página não repetem `id`. */
  prefixo: string;
  /** Alinha a primeira coluna ao formulário em página inteira (duas colunas) ou em janela (uma). */
  duasColunas?: boolean;
}

/** Título de bloco vira identificador estável para o `aria-labelledby`. */
function identificar(titulo: string): string {
  return titulo
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

/**
 * Campos de um formulário de cadastro, nos blocos e na ordem do protótipo. O
 * rótulo vai em caixa normal (o protótipo aplica `text-transform`, não texto), e
 * o obrigatório leva asterisco visual e "(obrigatório)" para leitor de tela.
 */
export function CamposDoCadastro({
  campos,
  valores,
  problemas,
  aoAlterar,
  prefixo,
  duasColunas = true,
}: CamposDoCadastroProps) {
  const marcas = useOpcoesDeMarca(campos.some((campo) => campo.tipo === 'marca'));
  const modelos = useOpcoesDeModelo(campos.some((campo) => campo.tipo === 'modelo'));

  // Blocos na ordem em que os campos foram declarados, sem reordenar nada.
  const blocos: { titulo: string; campos: CampoDeFormulario[] }[] = [];
  for (const campo of campos) {
    const ultimo = blocos.at(-1);
    if (ultimo && ultimo.titulo === campo.bloco) ultimo.campos.push(campo);
    else blocos.push({ titulo: campo.bloco, campos: [campo] });
  }

  return (
    <div className="space-y-8">
      {blocos.map((bloco) => (
        <section
          key={bloco.titulo}
          aria-labelledby={`${prefixo}-bloco-${identificar(bloco.titulo)}`}
        >
          {/* Formulário de um bloco só ("Dados") não precisa de título: ele repetiria o da janela. */}
          {blocos.length > 1 && (
            <h2
              id={`${prefixo}-bloco-${identificar(bloco.titulo)}`}
              className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground"
            >
              {bloco.titulo}
            </h2>
          )}
          <div className={duasColunas ? 'grid gap-5 sm:grid-cols-2' : 'grid gap-5'}>
            {bloco.campos.map((campo) => {
              const id = `${prefixo}-${campo.nome}`;
              const erro = problemas[campo.nome];
              const idAjuda = `${id}-ajuda`;
              const idErro = `${id}-erro`;
              const descrito =
                [campo.ajuda ? idAjuda : null, erro ? idErro : null].filter(Boolean).join(' ') ||
                undefined;
              const valor = valores[campo.nome] ?? '';

              return (
                <div
                  key={campo.nome}
                  className={
                    campo.tipo === 'textarea' || campo.tipo === 'grafias'
                      ? 'space-y-1.5 sm:col-span-2'
                      : 'space-y-1.5'
                  }
                >
                  {campo.tipo === 'checkbox' ? (
                    <div className="flex items-center gap-2 pt-1">
                      <Checkbox
                        id={id}
                        checked={valor === 'sim'}
                        onCheckedChange={(marcado) => aoAlterar(campo.nome, marcado ? 'sim' : '')}
                        aria-describedby={descrito}
                      />
                      <Label htmlFor={id} className="font-normal">
                        {campo.rotuloDaMarcacao ?? campo.rotulo}
                      </Label>
                    </div>
                  ) : (
                    <>
                      <Label htmlFor={id}>
                        {campo.rotulo}
                        {campo.obrigatorio && (
                          <span className="ml-0.5 text-destructive-text" aria-hidden="true">
                            *
                          </span>
                        )}
                        {campo.obrigatorio && <span className="sr-only"> (obrigatório)</span>}
                      </Label>
                      {campo.tipo === 'textarea' || campo.tipo === 'grafias' ? (
                        <Textarea
                          id={id}
                          name={campo.nome}
                          rows={4}
                          maxLength={campo.tipo === 'grafias' ? undefined : campo.maxLength}
                          value={valor}
                          onChange={(e) => aoAlterar(campo.nome, e.target.value)}
                          aria-invalid={erro ? true : undefined}
                          aria-describedby={descrito}
                        />
                      ) : campo.tipo === 'select' ||
                        campo.tipo === 'marca' ||
                        campo.tipo === 'modelo' ? (
                        <Select
                          value={valor || undefined}
                          onValueChange={(novo) => aoAlterar(campo.nome, novo)}
                        >
                          <SelectTrigger
                            id={id}
                            aria-invalid={erro ? true : undefined}
                            aria-describedby={descrito}
                          >
                            <SelectValue
                              placeholder={
                                (campo.tipo === 'marca' && marcas.isLoading) ||
                                (campo.tipo === 'modelo' && modelos.isLoading)
                                  ? 'Carregando…'
                                  : 'Selecione…'
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {campo.tipo === 'marca'
                              ? (marcas.data?.itens ?? []).map((marca) => (
                                  <SelectItem key={marca.id} value={marca.id}>
                                    {String(marca.nome)}
                                  </SelectItem>
                                ))
                              : campo.tipo === 'modelo'
                                ? (modelos.data ?? []).map((modelo) => (
                                    <SelectItem key={modelo.id} value={modelo.id}>
                                      {String(modelo.marcaNome)} {String(modelo.nome)}
                                    </SelectItem>
                                  ))
                                : (campo.opcoes ?? []).map((opcao) => (
                                    <SelectItem key={opcao.valor} value={opcao.valor}>
                                      {opcao.texto}
                                    </SelectItem>
                                  ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          id={id}
                          name={campo.nome}
                          type={campo.tipo === 'number' ? 'number' : (campo.tipo ?? 'text')}
                          inputMode={campo.tipo === 'number' ? 'numeric' : undefined}
                          min={campo.min}
                          max={campo.max}
                          maxLength={campo.maxLength}
                          value={valor}
                          onChange={(e) => aoAlterar(campo.nome, e.target.value)}
                          aria-invalid={erro ? true : undefined}
                          aria-describedby={descrito}
                        />
                      )}
                    </>
                  )}
                  {campo.tipo === 'marca' && marcas.isError && (
                    <p className="text-xs text-destructive-text">
                      Não foi possível carregar as marcas. Feche e abra o formulário para tentar de
                      novo.
                    </p>
                  )}
                  {campo.tipo === 'marca' && marcas.data && marcas.data.itens.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      Nenhuma marca ativa. Cadastre uma marca antes de cadastrar o modelo.
                    </p>
                  )}
                  {campo.tipo === 'modelo' && modelos.isError && (
                    <p className="text-xs text-destructive-text">
                      Não foi possível carregar os modelos. Feche e abra o formulário para tentar de
                      novo.
                    </p>
                  )}
                  {campo.tipo === 'modelo' && modelos.data && modelos.data.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      Nenhum modelo ativo. Cadastre um modelo antes de cadastrar a versão.
                    </p>
                  )}
                  {campo.ajuda && (
                    <p id={idAjuda} className="text-xs text-muted-foreground">
                      {campo.ajuda}
                    </p>
                  )}
                  {erro && (
                    <p id={idErro} className="text-xs text-destructive-text">
                      {erro}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
